/**
 * Stage-history timeline — the append-only audit trail (rule R3).
 * Each entry: from → to, who, when, and why. Newest first.
 */

import React from 'react';
import { ArrowRight } from 'lucide-react';
import type { StageHistoryRow } from '../../../services/recruitmentApi';
import { StageBadge } from './badges';
import { formatDateTime, recruiterName } from '../utils/format';
import { EmptyState } from './states';

export const StageTimeline: React.FC<{ history: StageHistoryRow[] }> = ({ history }) => {
	if (!history || history.length === 0) {
		return <EmptyState title='No stage history yet' message='Stage moves will appear here as an audit trail.' />;
	}

	// Show newest first (the API returns oldest → newest).
	const rows = [...history].reverse();

	return (
		<ol className='relative space-y-5'>
			{rows.map((h, idx) => (
				<li key={`${h.performed_at}-${idx}`} className='relative flex gap-3 pl-2'>
					{/* rail */}
					<div className='flex flex-col items-center'>
						<span className='mt-1 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-primary-100' />
						{idx < rows.length - 1 && <span className='mt-1 w-px flex-1 bg-gray-200' />}
					</div>

					<div className='flex-1 pb-1'>
						<div className='flex flex-wrap items-center gap-2'>
							{h.from_stage ? (
								<>
									<StageBadge stage={h.from_stage} />
									<ArrowRight className='h-3.5 w-3.5 text-gray-400' />
								</>
							) : (
								<span className='text-xs font-medium uppercase tracking-wide text-gray-400'>Created</span>
							)}
							<StageBadge stage={h.to_stage} />
						</div>

						{h.reason && <p className='mt-1.5 text-sm text-gray-700'>{h.reason}</p>}

						<p className='mt-1 text-xs text-gray-400'>
							{recruiterName(h.by_first_name, h.by_last_name)} · {formatDateTime(h.performed_at)}
						</p>
					</div>
				</li>
			))}
		</ol>
	);
};
